import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  deleteCompensation,
  deleteAdditionalCompensation,
  getEmployeeCompensations,
  getEmployeeAdditionalCompensations,
  updateEmployee,
  type Compensation,
  type AdditionalCompensation,
} from '@/lib/employees';
import { formatDate, toDateKey, isPastDateOnly, daysUntilDateOnly } from '@/lib/date';
import { useCanEditCompensations } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import type { EmployeeDetail } from '@/types/employee';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  Clock,
  DollarSign,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  RotateCw,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { IconActionButton } from './icon-action-button';
import { CompensationDialog } from './compensation-dialog';
import { AdditionalCompensationDialog } from './additional-compensation-dialog';
import { CustomBlocksSection } from './custom-blocks/custom-blocks-section';

interface CompensationsTabProps {
  employee: EmployeeDetail;
}

// ─── Formatting helpers ──────────────────────────────────────────────────────

function formatAmount(amount: string | number, currency: string): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}

function maskAmount(currency: string): string {
  const symbol =
    new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0 })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? '$';
  return `${symbol}\u2009••••`;
}

function formatPaymentPeriod(period: string | null | undefined): string {
  if (!period) return '';
  const labels: Record<string, string> = {
    monthly: '/ mo',
    yearly: '/ yr',
    annually: '/ yr',
    hourly: '/ hr',
    weekly: '/ wk',
    biweekly: '/ 2wk',
    semi_monthly: '/ 2mo',
    hour: '/ hr',
    month: '/ mo',
    year: '/ yr',
    week: '/ wk',
  };
  return labels[period.toLowerCase()] || `/ ${period}`;
}

function parseAmount(amount: string | number): number {
  return typeof amount === 'string' ? parseFloat(amount) : amount;
}

const PERIOD_TO_YEARLY_MULTIPLIER: Record<string, number> = {
  hourly: 2080,
  hour: 2080,
  weekly: 52,
  week: 52,
  biweekly: 26,
  semi_monthly: 24,
  monthly: 12,
  month: 12,
  yearly: 1,
  annually: 1,
  year: 1,
};

function annualizeAmount(amount: number, period: string | null | undefined): number {
  if (!period) return amount;
  const multiplier = PERIOD_TO_YEARLY_MULTIPLIER[period.toLowerCase()];
  return multiplier ? amount * multiplier : amount;
}

function annualizeCompensation(comp: Compensation): number {
  return annualizeAmount(parseAmount(comp.amount), comp.paymentPeriod);
}

/** Today's calendar date (YYYY-MM-DD) in the viewer's local timezone. */
function todayKey(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
}

/**
 * Re-renders the caller when the local calendar day rolls over, so date-derived state (current vs
 * scheduled rate, active bonuses) recomputes at midnight on an open, focused page — instead of
 * staying stale until the next refetch/refocus. Returns today's key for memo/effect dependencies.
 * HW-652.
 */
function useTodayKey(): string {
  const [today, setToday] = useState(todayKey);
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    const scheduleNextRollover = () => {
      const now = new Date();
      // 50 ms past the next local midnight, so the new calendar day has definitely started.
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 50);
      timeoutId = setTimeout(() => {
        setToday(todayKey());
        scheduleNextRollover();
      }, nextMidnight.getTime() - now.getTime());
    };
    scheduleNextRollover();
    return () => clearTimeout(timeoutId);
  }, []);
  return today;
}

/** A compensation row is "scheduled" when its effectiveDate is in the future. */
function isScheduled(comp: Compensation, today: string = todayKey()): boolean {
  return toDateKey(comp.effectiveDate) > today;
}

/** A bonus row is "scheduled" when startsOn is in the future. */
function isBonusScheduled(b: AdditionalCompensation, today: string = todayKey()): boolean {
  return toDateKey(b.startsOn) > today;
}

/**
 * True when a reoccurring bonus is currently active:
 *   startsOn <= today AND (endsOn is null OR endsOn >= today).
 */
function isBonusActive(b: AdditionalCompensation, today: string = todayKey()): boolean {
  if (b.type !== 'reoccurring') return false;
  if (toDateKey(b.startsOn) > today) return false;
  if (b.endsOn && toDateKey(b.endsOn) < today) return false;
  return true;
}

function getChangePercent(current: Compensation, previous: Compensation): number | null {
  const curr = annualizeCompensation(current);
  const prev = annualizeCompensation(previous);
  if (!prev || prev === 0) return null;
  return Math.round(((curr - prev) / prev) * 100);
}

/**
 * Annualised value of a recurring bonus: amount × payments-per-year.
 * Frequency falls back to monthly when unset.
 */
function annualizeBonus(b: AdditionalCompensation): number {
  const amount = parseAmount(b.amount);
  const freq = (b.frequency || 'monthly').toLowerCase();
  const mult = PERIOD_TO_YEARLY_MULTIPLIER[freq] ?? 12;
  return amount * mult;
}

// ─── Tiny UI atoms ───────────────────────────────────────────────────────────

function Pill({
  children,
  tone = 'muted',
  className,
}: {
  children: React.ReactNode;
  tone?: 'muted' | 'current' | 'scheduled' | 'ok' | 'active' | 'type-bonus';
  className?: string;
}) {
  // Status pills (current / scheduled / active) reuse the same `es-*` recipe
  // the employee-status badge on the profile hero card uses — solid deep-teal
  // for "active", solid amber-tan for "upcoming/scheduled". Keeps the cosmetic
  // language consistent across the profile.
  // Metric / category pills (delta %, type, past) stay tinted so they don't
  // compete with statuses for attention.
  const tones: Record<string, { pill: string; dot: string | null }> = {
    muted: { pill: 'bg-muted text-muted-foreground border border-border/60', dot: null },
    current: { pill: 'es-active', dot: 'es-dot-active' },
    scheduled: { pill: 'es-probation', dot: 'es-dot-probation' },
    ok: {
      pill: 'bg-emerald-100 text-emerald-900 border border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-500/30',
      dot: null,
    },
    active: { pill: 'es-active', dot: 'es-dot-active' },
    'type-bonus': {
      pill: 'bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30',
      dot: null,
    },
  };
  const { pill, dot } = tones[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-medium leading-none',
        pill,
        className,
      )}
    >
      {dot && <span className={cn('h-1.5 w-1.5 rounded-full', dot)} />}
      {children}
    </span>
  );
}

function IconButton({
  onClick,
  label,
  children,
  destructive,
}: {
  onClick: () => void;
  label: string;
  children: React.ReactNode;
  destructive?: boolean;
}) {
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={onClick}
            aria-label={label}
            className={cn(
              'inline-grid place-items-center h-6 w-6 rounded text-muted-foreground transition-colors cursor-pointer',
              destructive
                ? 'hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950'
                : 'hover:bg-muted hover:text-foreground',
            )}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ─── Next-review inline editor ───────────────────────────────────────────────

function NextReviewInline({
  employee,
  canEdit,
}: {
  employee: EmployeeDetail;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState(employee.nextReviewDate?.split('T')[0] ?? '');

  const mutation = useMutation({
    mutationFn: (nextReviewDate: string | null) =>
      updateEmployee(employee.id, { nextReviewDate }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees', 'detail', employee.id] });
      setEditing(false);
    },
  });

  const hasReviewDate = !!employee.nextReviewDate;
  // Compare on a calendar-day basis so a review due today is not "overdue":
  // nextReviewDate is a date-only @db.Date (UTC midnight), never a wall-clock instant.
  const isOverdue = isPastDateOnly(employee.nextReviewDate);
  const daysUntilReview = daysUntilDateOnly(employee.nextReviewDate);
  const isUpcoming = !isOverdue && daysUntilReview !== null && daysUntilReview <= 30;

  if (editing && canEdit) {
    return (
      <div className="flex items-center gap-1.5">
        <CalendarClock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-muted-foreground text-xs">Next review:</span>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          disabled={mutation.isPending}
          className="h-6 w-36 text-xs dark:[color-scheme:dark]"
        />
        <button
          onClick={() => mutation.mutate(date || null)}
          disabled={mutation.isPending}
          className="text-[11px] font-medium text-primary hover:underline cursor-pointer"
        >
          Save
        </button>
        <button
          onClick={() => {
            setDate(employee.nextReviewDate?.split('T')[0] ?? '');
            setEditing(false);
          }}
          className="text-[11px] text-muted-foreground hover:underline cursor-pointer"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <CalendarClock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-muted-foreground text-xs">Next review:</span>
      <span
        className={cn(
          'text-xs font-medium tabular-nums',
          isOverdue && 'text-red-600 dark:text-red-400',
          isUpcoming && !isOverdue && 'text-amber-700 dark:text-amber-400',
        )}
      >
        {hasReviewDate ? formatDate(employee.nextReviewDate, 'long') : 'Not scheduled'}
      </span>
      {isOverdue && <Pill tone="scheduled" className="!bg-red-100 !text-red-700">Overdue</Pill>}
      {canEdit && (
        <IconActionButton
          icon={Pencil}
          tooltip="Edit review date"
          onClick={() => setEditing(true)}
          size="sm"
        />
      )}
      {hasReviewDate && canEdit && (
        <IconActionButton
          icon={X}
          tooltip="Clear review date"
          onClick={() => mutation.mutate(null)}
          disabled={mutation.isPending}
          variant="destructive"
          size="sm"
        />
      )}
    </div>
  );
}

// ─── Loading skeleton ────────────────────────────────────────────────────────

function CompensationsTabSkeleton() {
  return (
    <div className="rounded-lg border border-border/50 bg-card overflow-hidden">
      <div className="border-b border-border/50 bg-muted/30 px-5 py-3 flex items-center justify-between">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-7 w-20" />
      </div>
      <div className="p-5 space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <Skeleton className="h-4 w-4 rounded-full" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-4 w-20 ml-auto" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

type SubTab = 'base' | 'additional';

export function CompensationsTab({ employee }: CompensationsTabProps) {
  const queryClient = useQueryClient();
  const canEdit = useCanEditCompensations();
  // Ticks at local midnight so the current/scheduled split re-derives without a refetch. HW-652.
  const today = useTodayKey();

  const [subtab, setSubtab] = useState<SubTab>('base');
  const [revealed, setRevealed] = useState(false);
  const [baseDialog, setBaseDialog] = useState<{ open: boolean; entry?: Compensation | null }>({
    open: false,
  });
  const [bonusDialog, setBonusDialog] = useState<{
    open: boolean;
    entry?: AdditionalCompensation | null;
  }>({ open: false });
  const [deletingBase, setDeletingBase] = useState<Compensation | null>(null);
  const [deletingBonus, setDeletingBonus] = useState<AdditionalCompensation | null>(null);

  const { data: compensations = [], isLoading: loadingBase } = useQuery({
    queryKey: ['employee', employee.id, 'compensations'],
    queryFn: () => getEmployeeCompensations(employee.id),
  });

  const { data: bonuses = [], isLoading: loadingBonuses } = useQuery({
    queryKey: ['employee', employee.id, 'additional-compensations'],
    queryFn: () => getEmployeeAdditionalCompensations(employee.id),
  });

  // ─── Derived: current base + scheduled base ────────────────────────────
  const { currentCompId, currentComp, nextScheduled } = useMemo(() => {
    const scheduled = compensations.filter((c) => isScheduled(c, today));
    const effective = compensations.filter((c) => !isScheduled(c, today));
    const current = effective[0] ?? null;
    return {
      currentCompId: current?.id ?? null,
      currentComp: current,
      // Earliest of the future-dated entries.
      nextScheduled: scheduled.length > 0 ? scheduled[scheduled.length - 1] : null,
    };
  }, [compensations, today]);

  // ─── Derived: current bonuses + grouping ───────────────────────────────
  const { reoccurringBonuses, fixedBonuses, activeBonusMonthly, activeBonusCurrency } =
    useMemo(() => {
      const reoccurring = bonuses.filter((b) => b.type === 'reoccurring');
      const fixed = bonuses.filter((b) => b.type !== 'reoccurring');
      // Sum of currently active recurring bonuses, normalised to per-month.
      // Mixed currencies → just take the most common currency for display;
      // the strip is informational, not a payroll figure.
      let monthly = 0;
      const currencyCount: Record<string, number> = {};
      for (const b of reoccurring) {
        if (!isBonusActive(b, today)) continue;
        const annual = annualizeBonus(b);
        monthly += annual / 12;
        currencyCount[b.currency] = (currencyCount[b.currency] ?? 0) + 1;
      }
      const currency =
        Object.entries(currencyCount).sort((a, b) => b[1] - a[1])[0]?.[0] ??
        currentComp?.currency ??
        'USD';
      return {
        reoccurringBonuses: reoccurring,
        fixedBonuses: fixed,
        activeBonusMonthly: monthly,
        activeBonusCurrency: currency,
      };
    }, [bonuses, currentComp, today]);

  // ─── Derived: earnings strip ───────────────────────────────────────────
  const earnings = useMemo(() => {
    if (!currentComp) {
      return null;
    }
    const baseAnnual = annualizeCompensation(currentComp);
    const baseMonthly = baseAnnual / 12;
    const bonusAnnual = activeBonusMonthly * 12;
    const totalMonthly = baseMonthly + activeBonusMonthly;
    const totalAnnual = baseAnnual + bonusAnnual;

    // After-scheduled projection (if a scheduled base change exists).
    let after: { monthly: number; annual: number; deltaPct: number } | null = null;
    if (nextScheduled) {
      const afterBaseAnnual = annualizeCompensation(nextScheduled);
      const afterAnnual = afterBaseAnnual + bonusAnnual;
      const afterMonthly = afterAnnual / 12;
      after = {
        monthly: afterMonthly,
        annual: afterAnnual,
        deltaPct:
          totalAnnual > 0 ? Math.round(((afterAnnual - totalAnnual) / totalAnnual) * 100) : 0,
      };
    }

    return {
      currency: currentComp.currency,
      bonusCurrency: activeBonusCurrency,
      baseMonthly,
      bonusMonthly: activeBonusMonthly,
      totalMonthly,
      totalAnnual,
      after,
    };
  }, [currentComp, activeBonusMonthly, activeBonusCurrency, nextScheduled]);

  // ─── Delete mutations ──────────────────────────────────────────────────
  const deleteBaseMutation = useMutation({
    mutationFn: (id: string) => deleteCompensation(employee.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee', employee.id, 'compensations'] });
      setDeletingBase(null);
    },
  });
  const deleteBonusMutation = useMutation({
    mutationFn: (id: string) => deleteAdditionalCompensation(employee.id, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['employee', employee.id, 'additional-compensations'],
      });
      setDeletingBonus(null);
    },
  });

  if (loadingBase) return <CompensationsTabSkeleton />;

  const formatRevealed = (amount: string | number, currency: string) =>
    revealed ? formatAmount(amount, currency) : maskAmount(currency);

  const onAdd = () =>
    subtab === 'base' ? setBaseDialog({ open: true }) : setBonusDialog({ open: true });

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-border/50 bg-card overflow-hidden">
        {/* ── Header: title + reveal + add ─────────────────────────── */}
        <header className="border-b border-border/50 bg-muted/30 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-hygge" strokeWidth={2.2} />
            <h2 className="font-semibold text-[15px]">Compensation</h2>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setRevealed((v) => !v)}
              className="inline-grid place-items-center h-7 w-7 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors cursor-pointer"
              aria-label={revealed ? 'Hide amounts' : 'Reveal amounts'}
            >
              {revealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
            {canEdit && (
              <button
                onClick={onAdd}
                className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-cta text-cta-foreground text-[12px] font-medium hover:opacity-90 cursor-pointer"
              >
                <Plus className="h-3 w-3" strokeWidth={2.4} />
                {subtab === 'base' ? 'Add rate' : 'Add bonus'}
              </button>
            )}
          </div>
        </header>

        {/* ── Earnings strip — first thing under the header, always visible ── */}
        <EarningsStrip earnings={earnings} revealed={revealed} />

        {/* ── Sub-tabs — minimal underlined style, matches Absences config page ── */}
        <nav
          role="tablist"
          className="flex items-end gap-6 border-b border-border px-5 pt-3"
        >
          <button
            type="button"
            role="tab"
            aria-selected={subtab === 'base'}
            onClick={() => setSubtab('base')}
            className={cn(
              'relative inline-flex items-center gap-1.5 px-0 pb-3 pt-0 text-sm font-medium transition-colors cursor-pointer',
              'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-hygge after:rounded-t-full after:transition-opacity',
              subtab === 'base'
                ? 'text-foreground after:opacity-100'
                : 'text-muted-foreground hover:text-foreground after:opacity-0',
            )}
          >
            Base
            <span className="tabular-nums text-[11px] text-muted-foreground">
              {compensations.length}
            </span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={subtab === 'additional'}
            onClick={() => setSubtab('additional')}
            className={cn(
              'relative inline-flex items-center gap-1.5 px-0 pb-3 pt-0 text-sm font-medium transition-colors cursor-pointer',
              'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-hygge after:rounded-t-full after:transition-opacity',
              subtab === 'additional'
                ? 'text-foreground after:opacity-100'
                : 'text-muted-foreground hover:text-foreground after:opacity-0',
            )}
          >
            Additional
            <span className="tabular-nums text-[11px] text-muted-foreground">
              {bonuses.length}
            </span>
            {reoccurringBonuses.some((b) => isBonusActive(b, today)) && (
              <Pill tone="active">
                {reoccurringBonuses.filter((b) => isBonusActive(b, today)).length} active
              </Pill>
            )}
          </button>
        </nav>

        {/* ── Inline meta (Next review, only on Base tab) ─────────── */}
        {subtab === 'base' && (
          <div className="px-5 py-2.5 border-b border-border/50 bg-muted/15 flex items-center gap-4 flex-wrap">
            <NextReviewInline employee={employee} canEdit={canEdit} />
            {nextScheduled && (
              <>
                <span className="h-3 w-px bg-border" />
                <div className="flex items-center gap-1.5 text-xs">
                  <Clock className="h-3.5 w-3.5 text-amber-600" />
                  <span className="text-muted-foreground">Scheduled change:</span>
                  <span className="font-medium tabular-nums">
                    {formatDate(nextScheduled.effectiveDate, 'long')}
                  </span>
                  <span className="text-muted-foreground tabular-nums">·</span>
                  <span className="tabular-nums">
                    {currentComp && (
                      <>
                        <span className="text-muted-foreground line-through">
                          {formatRevealed(currentComp.amount, currentComp.currency)}
                        </span>{' '}
                        <span className="text-muted-foreground">→</span>{' '}
                      </>
                    )}
                    <span className="font-semibold">
                      {formatRevealed(nextScheduled.amount, nextScheduled.currency)}
                      <span className="text-muted-foreground font-normal ml-0.5 text-[11px]">
                        {formatPaymentPeriod(nextScheduled.paymentPeriod)}
                      </span>
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── Tab content ─────────────────────────────────────────── */}
        {subtab === 'base' ? (
          <BaseList
            compensations={compensations}
            currentCompId={currentCompId}
            canEdit={canEdit}
            onEdit={(entry) => setBaseDialog({ open: true, entry })}
            onDelete={(entry) => setDeletingBase(entry)}
            formatRevealed={formatRevealed}
            today={today}
          />
        ) : (
          <AdditionalList
            reoccurring={reoccurringBonuses}
            fixed={fixedBonuses}
            loading={loadingBonuses}
            canEdit={canEdit}
            onEdit={(entry) => setBonusDialog({ open: true, entry })}
            onDelete={(entry) => setDeletingBonus(entry)}
            formatRevealed={formatRevealed}
            today={today}
          />
        )}
      </section>

      {/* Dialogs */}
      <CompensationDialog
        open={baseDialog.open}
        onOpenChange={(open) => setBaseDialog({ open })}
        employeeId={employee.id}
        compensation={baseDialog.entry}
      />
      <AdditionalCompensationDialog
        open={bonusDialog.open}
        onOpenChange={(open) => setBonusDialog({ open })}
        employeeId={employee.id}
        entry={bonusDialog.entry}
      />

      {/* Delete base */}
      <AlertDialog
        open={!!deletingBase}
        onOpenChange={(open) => !open && setDeletingBase(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete compensation record</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently remove this rate entry
              {deletingBase && (
                <>
                  {' '}({formatAmount(deletingBase.amount, deletingBase.currency)} effective{' '}
                  {formatDate(deletingBase.effectiveDate, 'monthYear')})
                </>
              )}
              ? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBaseMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deletingBase && deleteBaseMutation.mutate(deletingBase.id)}
              disabled={deleteBaseMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete bonus */}
      <AlertDialog
        open={!!deletingBonus}
        onOpenChange={(open) => !open && setDeletingBonus(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete bonus entry</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently remove
              {deletingBonus && (
                <>
                  {' '}"{deletingBonus.description ?? deletingBonus.compensationType}" (
                  {formatAmount(deletingBonus.amount, deletingBonus.currency)}
                  {deletingBonus.type === 'reoccurring' && deletingBonus.frequency
                    ? ` ${formatPaymentPeriod(deletingBonus.frequency)}`
                    : ''}
                  )
                </>
              )}
              ? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBonusMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => deletingBonus && deleteBonusMutation.mutate(deletingBonus.id)}
              disabled={deleteBonusMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CustomBlocksSection section="COMPENSATIONS" employeeId={employee.id} />
    </div>
  );
}

// ─── Earnings summary strip ──────────────────────────────────────────────────

interface EarningsSummary {
  currency: string;
  bonusCurrency: string;
  baseMonthly: number;
  bonusMonthly: number;
  totalMonthly: number;
  totalAnnual: number;
  after: { monthly: number; annual: number; deltaPct: number } | null;
}

function EarningsStrip({
  earnings,
  revealed,
}: {
  earnings: EarningsSummary | null;
  revealed: boolean;
}) {
  if (!earnings) {
    return (
      <div className="px-5 py-3 border-b border-border/50 bg-hygge/5">
        <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
          Earning right now
        </div>
        <div className="text-sm text-muted-foreground mt-1">
          No base compensation recorded yet.
        </div>
      </div>
    );
  }

  const e = earnings;

  const fmtAmount = (n: number, currency: string) =>
    revealed
      ? new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(n)
      : maskAmount(currency);

  return (
    <div className="px-5 py-3 border-b border-border/50 bg-hygge/5 grid items-center gap-5 grid-cols-[minmax(0,1fr)_auto_minmax(0,auto)]">
      <div className="flex items-center gap-5 flex-wrap">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
            Earning right now
          </div>
          <div className="mt-0.5 flex items-baseline gap-2 tabular-nums">
            <span className="text-[20px] font-semibold text-hygge">
              {fmtAmount(e.totalMonthly, e.currency)}
            </span>
            <span className="text-xs text-muted-foreground">/ mo</span>
            <span className="text-muted-foreground text-xs">·</span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {fmtAmount(e.totalAnnual, e.currency)} / yr
            </span>
          </div>
        </div>
        <div className="h-8 w-px bg-border" />
        <div className="flex items-center gap-4 text-[12.5px]">
          <div className="flex items-baseline gap-1.5">
            <span className="h-2 w-2 rounded-full bg-hygge self-center" />
            <span className="tabular-nums font-medium">
              {fmtAmount(e.baseMonthly, e.currency)}
            </span>
            <span className="text-muted-foreground text-[11.5px]">base / mo</span>
          </div>
          <span className="text-muted-foreground">+</span>
          <div className="flex items-baseline gap-1.5">
            <span className="h-2 w-2 rounded-full bg-amber-500 self-center" />
            <span className="tabular-nums font-medium">
              {fmtAmount(e.bonusMonthly, e.bonusCurrency)}
            </span>
            <span className="text-muted-foreground text-[11.5px]">bonus / mo</span>
          </div>
        </div>
      </div>
      {e.after ? (
        <>
          <div className="h-8 w-px bg-border" />
          <div className="text-right">
            <div className="text-[10.5px] uppercase tracking-wider text-amber-700/85 dark:text-amber-400/85 flex items-center gap-1 justify-end">
              <Clock className="h-3 w-3" />
              After change
            </div>
            <div className="mt-0.5 flex items-baseline gap-2 tabular-nums justify-end">
              <span className="text-[16px] font-semibold">
                {fmtAmount(e.after.monthly, e.currency)}
              </span>
              <span className="text-xs text-muted-foreground">/ mo</span>
              {e.after.deltaPct !== 0 && (
                <Pill tone="ok">
                  {e.after.deltaPct > 0 ? '↑' : '↓'} {Math.abs(e.after.deltaPct)}%
                </Pill>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

// ─── Base sub-tab list ───────────────────────────────────────────────────────

function BaseList({
  compensations,
  currentCompId,
  canEdit,
  onEdit,
  onDelete,
  formatRevealed,
  today,
}: {
  compensations: Compensation[];
  currentCompId: string | null;
  canEdit: boolean;
  onEdit: (entry: Compensation) => void;
  onDelete: (entry: Compensation) => void;
  formatRevealed: (amount: string | number, currency: string) => string;
  today: string;
}) {
  if (compensations.length === 0) {
    return <EmptyState text="No compensation records yet" canEdit={canEdit} actionLabel="Add rate" />;
  }
  return (
    <>
      {/* Column header */}
      <div
        className="px-5 py-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground border-b border-border/40 grid items-center gap-3"
        style={{ gridTemplateColumns: '18px minmax(0,1fr) 110px auto' }}
      >
        <span />
        <span>Effective</span>
        <span className="text-right">Rate</span>
        <span />
      </div>
      <ul>
        {compensations.map((comp, index) => {
          const isCurrent = comp.id === currentCompId;
          const isLast = index === compensations.length - 1;
          const scheduled = isScheduled(comp, today);
          const prev = index < compensations.length - 1 ? compensations[index + 1] : null;
          const changePct = prev ? getChangePercent(comp, prev) : null;
          return (
            <BaseRow
              key={comp.id}
              comp={comp}
              isCurrent={isCurrent}
              isScheduled={scheduled}
              isLast={isLast}
              changePct={changePct}
              canEdit={canEdit}
              onEdit={() => onEdit(comp)}
              onDelete={() => onDelete(comp)}
              formatRevealed={formatRevealed}
              today={today}
            />
          );
        })}
      </ul>
      <div className="border-t border-border/50 bg-muted/20 px-5 py-2 text-[11.5px] text-muted-foreground flex items-center justify-between">
        <span>
          {compensations.length} {compensations.length === 1 ? 'entry' : 'entries'}
        </span>
      </div>
    </>
  );
}

function BaseRow({
  comp,
  isCurrent,
  isScheduled: scheduled,
  isLast,
  changePct,
  canEdit,
  onEdit,
  onDelete,
  formatRevealed,
  today,
}: {
  comp: Compensation;
  isCurrent: boolean;
  isScheduled: boolean;
  isLast: boolean;
  changePct: number | null;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  formatRevealed: (amount: string | number, currency: string) => string;
  today: string;
}) {
  const periodSuffix = formatPaymentPeriod(comp.paymentPeriod);
  // The active rate reads "— present" while it is still in force: its endDate is
  // null or still in the future (the day before a scheduled successor starts). A
  // current row whose endDate has already passed, or any past rate, shows its
  // closed historical range instead.
  const dateLabel = scheduled
    ? `from ${formatDate(comp.effectiveDate, 'long')}`
    : isCurrent && (!comp.endDate || toDateKey(comp.endDate) >= today)
      ? `${formatDate(comp.effectiveDate, 'long')} — present`
      : comp.endDate
        ? `${formatDate(comp.effectiveDate, 'long')} — ${formatDate(comp.endDate, 'long')}`
        : formatDate(comp.effectiveDate, 'long');

  return (
    <li
      className={cn(
        'px-5 py-2 grid items-center gap-3 transition-colors group',
        !isLast && 'border-b border-border/40',
        scheduled && 'bg-amber-50/35 dark:bg-amber-500/5',
        isCurrent && !scheduled && 'bg-hygge/5',
        'hover:bg-muted/30',
      )}
      style={{ gridTemplateColumns: '18px minmax(0,1fr) 110px auto' }}
    >
      <div className="relative h-4 w-4 grid place-items-center">
        {scheduled ? (
          <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" strokeWidth={2.2} />
        ) : isCurrent ? (
          <span className="h-2.5 w-2.5 rounded-full bg-hygge ring-2 ring-card" />
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-border" />
        )}
      </div>
      <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
        <span
          className={cn(
            'text-[13px]',
            scheduled && 'text-amber-700 dark:text-amber-400 font-medium',
            isCurrent && !scheduled && 'font-semibold',
            !isCurrent && !scheduled && 'text-foreground/80',
          )}
        >
          {dateLabel}
        </span>
        {comp.comment && (
          <span className="text-muted-foreground text-[11.5px] truncate">· {comp.comment}</span>
        )}
      </div>
      <div
        className={cn(
          'tabular-nums text-right',
          isCurrent ? 'text-[15px] font-semibold' : 'text-[14px] text-foreground/85',
          scheduled && 'text-foreground',
        )}
      >
        {formatRevealed(comp.amount, comp.currency)}
        {periodSuffix && (
          <span className="text-muted-foreground font-normal text-[11.5px] ml-0.5">
            {periodSuffix}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        {changePct !== null && changePct !== 0 && (
          <Pill tone={changePct > 0 ? 'ok' : 'muted'}>
            {changePct > 0 ? '↑' : '↓'} {Math.abs(changePct)}%
          </Pill>
        )}
        {scheduled && <Pill tone="scheduled">Scheduled</Pill>}
        {isCurrent && !scheduled && <Pill tone="current">Current</Pill>}
        {canEdit && (
          <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity ml-0.5">
            <IconButton onClick={onEdit} label="Edit">
              <Pencil className="h-3 w-3" />
            </IconButton>
            <IconButton onClick={onDelete} label="Delete" destructive>
              <Trash2 className="h-3 w-3" />
            </IconButton>
          </div>
        )}
      </div>
    </li>
  );
}

// ─── Additional sub-tab list ─────────────────────────────────────────────────

function AdditionalList({
  reoccurring,
  fixed,
  loading,
  canEdit,
  onEdit,
  onDelete,
  formatRevealed,
  today,
}: {
  reoccurring: AdditionalCompensation[];
  fixed: AdditionalCompensation[];
  loading: boolean;
  canEdit: boolean;
  onEdit: (entry: AdditionalCompensation) => void;
  onDelete: (entry: AdditionalCompensation) => void;
  formatRevealed: (amount: string | number, currency: string) => string;
  today: string;
}) {
  if (loading) {
    return (
      <div className="p-5 space-y-3">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (reoccurring.length === 0 && fixed.length === 0) {
    return (
      <EmptyState
        text="No bonuses or additional compensation yet"
        canEdit={canEdit}
        actionLabel="Add bonus"
      />
    );
  }

  return (
    <>
      {reoccurring.length > 0 && (
        <>
          <GroupHeader label="Reoccurring" count={reoccurring.length} suffix="Per period" />
          <ul>
            {reoccurring.map((entry, i) => (
              <BonusRow
                key={entry.id}
                entry={entry}
                isLast={i === reoccurring.length - 1 && fixed.length === 0}
                canEdit={canEdit}
                onEdit={() => onEdit(entry)}
                onDelete={() => onDelete(entry)}
                formatRevealed={formatRevealed}
                today={today}
              />
            ))}
          </ul>
        </>
      )}
      {fixed.length > 0 && (
        <>
          <GroupHeader label="One-time" count={fixed.length} suffix="Amount" />
          <ul>
            {fixed.map((entry, i) => (
              <BonusRow
                key={entry.id}
                entry={entry}
                isLast={i === fixed.length - 1}
                canEdit={canEdit}
                onEdit={() => onEdit(entry)}
                onDelete={() => onDelete(entry)}
                formatRevealed={formatRevealed}
                today={today}
              />
            ))}
          </ul>
        </>
      )}
      <div className="border-t border-border/50 bg-muted/20 px-5 py-2 text-[11.5px] text-muted-foreground flex items-center justify-between">
        <span>
          {reoccurring.length + fixed.length} bonus
          {reoccurring.length + fixed.length === 1 ? '' : 'es'}
        </span>
      </div>
    </>
  );
}

function GroupHeader({ label, count, suffix }: { label: string; count: number; suffix: string }) {
  return (
    <div
      className="px-5 py-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground border-b border-border/40 grid items-center gap-3"
      style={{ gridTemplateColumns: '18px minmax(0,1fr) 110px auto' }}
    >
      <span />
      <span>
        {label} <span className="text-muted-foreground/70">· {count}</span>
      </span>
      <span className="text-right">{suffix}</span>
      <span />
    </div>
  );
}

function BonusRow({
  entry,
  isLast,
  canEdit,
  onEdit,
  onDelete,
  formatRevealed,
  today,
}: {
  entry: AdditionalCompensation;
  isLast: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
  formatRevealed: (amount: string | number, currency: string) => string;
  today: string;
}) {
  const active = isBonusActive(entry, today);
  const scheduled = isBonusScheduled(entry, today);
  const isReoccurring = entry.type === 'reoccurring';

  // Date label per type
  const dateLabel = (() => {
    if (isReoccurring) {
      if (entry.endsOn) {
        return `${formatDate(entry.startsOn, 'monthYear')} — ${formatDate(entry.endsOn, 'monthYear')}`;
      }
      return `from ${formatDate(entry.startsOn, 'monthYear')}`;
    }
    return `paid ${formatDate(entry.startsOn, 'monthYear')}`;
  })();

  const periodSuffix = isReoccurring ? formatPaymentPeriod(entry.frequency) : '';

  return (
    <li
      className={cn(
        'px-5 py-2 grid items-center gap-3 transition-colors group',
        !isLast && 'border-b border-border/40',
        active && 'bg-emerald-50/30 dark:bg-emerald-500/5',
        scheduled && 'bg-amber-50/30',
        'hover:bg-muted/30',
      )}
      style={{ gridTemplateColumns: '18px minmax(0,1fr) 110px auto' }}
    >
      <div className="h-4 w-4 grid place-items-center">
        {isReoccurring ? (
          <RotateCw
            className={cn(
              'h-3.5 w-3.5',
              active ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground',
            )}
            strokeWidth={2}
          />
        ) : (
          <Clock className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={2} />
        )}
      </div>
      <div className="min-w-0 flex items-baseline gap-2 flex-wrap">
        <span
          className={cn(
            'text-[13px]',
            active ? 'font-medium' : 'text-foreground/80',
          )}
        >
          {entry.description || entry.compensationType}
        </span>
        <span className="text-muted-foreground text-[11.5px] tabular-nums">· {dateLabel}</span>
      </div>
      <div
        className={cn(
          'tabular-nums text-right',
          active ? 'text-[14.5px] font-semibold' : 'text-[14px] text-foreground/85',
        )}
      >
        {formatRevealed(entry.amount, entry.currency)}
        {periodSuffix && (
          <span className="text-muted-foreground font-normal text-[11.5px] ml-0.5">
            {periodSuffix}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <Pill tone="type-bonus">{entry.compensationType}</Pill>
        {scheduled ? (
          <Pill tone="scheduled">Scheduled</Pill>
        ) : active ? (
          <Pill tone="active">Active</Pill>
        ) : isReoccurring ? (
          <Pill tone="muted">Closed</Pill>
        ) : (
          <Pill tone="muted">Paid</Pill>
        )}
        {canEdit && (
          <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity ml-0.5">
            <IconButton onClick={onEdit} label="Edit">
              <Pencil className="h-3 w-3" />
            </IconButton>
            <IconButton onClick={onDelete} label="Delete" destructive>
              <Trash2 className="h-3 w-3" />
            </IconButton>
          </div>
        )}
      </div>
    </li>
  );
}

function EmptyState({
  text,
  canEdit,
  actionLabel,
}: {
  text: string;
  canEdit: boolean;
  actionLabel: string;
}) {
  return (
    <div className="py-10 px-5 text-center">
      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
        <DollarSign className="h-5 w-5 text-muted-foreground/60" />
      </div>
      <p className="mt-3 text-sm font-medium">{text}</p>
      <p className="mt-1 text-xs text-muted-foreground">
        {canEdit ? `Click "${actionLabel}" above to get started.` : 'Nothing to show yet.'}
      </p>
    </div>
  );
}

